import { type AiModelSchema } from "./ai_model";
import { type BroadcastSchema } from "./broadcast";
import { type BroadcastMessageSchema } from "./broadcast_message";
import { CategorySchema } from "./category";
import { type ChatSchema } from "./chat";
import { type ChatLocationSchema } from "./chat_location";
import { type CustomerSchema } from "./customer";
import { type CustomerContactSchema } from "./customer_contact";
import { CustomerEmailSchema } from "./customer_email";
import { CustomerEmailLocationSchema } from "./customer_email_location";
import { type CustomerLocationSchema } from "./customer_location";
import { ErpIntegrationLogSchema } from "./erp_integration_log";
import { InviteMemberSchema } from "./invite_member";
import { InvoiceSchema } from "./invoice";
import { type ManagedEmailSchema } from "./managed_email";
import { type MessageSchema } from "./message";
import { MessageMessageTagSchema, MessageTagSchema } from "./message_tag";
import { MessageV2Schema } from "./message_v2";
import { NotificationTemplateSchema } from "./notification_template";
import { type OrderSchema } from "./order";
import { OrderActivityLogSchema } from "./order_activity_log";
import { OrderCorrectionEventSchema } from "./order_correction_event";
import { OrderGuideSchema } from "./order_guide";
import { type OrderMessageClassificationSchema } from "./order_message_classification";
import { ERPConfigSchema, type OrgConfigSchema, type PinnedChatSchema } from "./org_config";
import { type ProcessingMessageSchema } from "./processing_message";
import { ProductSchema } from "./product";
import { type ProductInOrderSchema } from "./product_in_order";
import { StandingOrderSchema } from "./standing_order";
import { StandingOrderProductSchema } from "./standing_order_product";
import { type CustomerTagSchema, type TagSchema } from "./tag";
import { type TextToOrderSampleSchema } from "./text_to_order_sample";
import { UnitSchema } from "./unit";
import { UnitCustomLabelSchema } from "./unit_custom_label";
import { UnitExpansionSchema } from "./unit_expansion";
import { type UserManagedEmailSchema } from "./user_managed_email";
import { type UserOrgSchema } from "./user_org";
import { UserPermissionSchema } from "./user_permission";
import { type UserProfileSchema } from "./user_profile";
import { WhatsappAccountSchema } from "./whatsapp_account";

export type ProductInOrder = typeof ProductInOrderSchema.$inferSelect & { product?: Product, order?: Order }
export type Product = typeof ProductSchema.$inferSelect & { units: Unit[], category: Category[] }
export type PinnedChat = typeof PinnedChatSchema.$inferSelect
export type OrgConfig = typeof OrgConfigSchema.$inferSelect & { pinned_chat?: PinnedChat[] }
export type ErpConfig = typeof ERPConfigSchema.$inferSelect
export type UserOrg = typeof UserOrgSchema.$inferSelect & { org_config: OrgConfig }
export type UserManagedEmail = typeof UserManagedEmailSchema.$inferSelect & {
  user_profile?: UserProfile;
  managed_email?: ManagedEmail;
};
export type OrderActivityLog = typeof OrderActivityLogSchema.$inferSelect & { order?: Order | null, user?: UserProfile | null }
export type OrderActivityLogInsert = typeof OrderActivityLogSchema.$inferInsert
export type ManagedEmail = typeof ManagedEmailSchema.$inferSelect & { user_org?: UserOrg; user_managed_emails?: UserManagedEmail[] };
export type Tag = typeof TagSchema.$inferInsert
export type CustomerTag = typeof CustomerTagSchema.$inferSelect & { customer?: Customer, tag?: Tag }
export type MessageTag = typeof MessageTagSchema.$inferSelect
export type MessageMessageTag = typeof MessageMessageTagSchema.$inferInsert & { message_tag?: MessageTag }
export type Broadcast = typeof BroadcastSchema.$inferSelect & { broadcast_messages?: BroadcastMessage[] }
export type BroadcastMessage = typeof BroadcastMessageSchema.$inferSelect & { broadcast?: Broadcast }
export type CustomerContact = typeof CustomerContactSchema.$inferSelect & { customer?: Customer }
export type Order = typeof OrderSchema.$inferSelect & {
  invoices?: Invoice[];
  customer?: Customer;
  products_in_order?: ProductInOrder[];
  message?: Message;
  message_v2?: MessageV2;
  standing_order?: StandingOrder;
  customer_location?: CustomerLocation;
  created_by_user?: UserProfile;
  updated_by_user?: UserProfile;
  correction_events?: OrderCorrectionEvent[];
  activity_logs?: OrderActivityLog[] | null;
  whatsapp_account?: WhatsappAccount;
}
export type Invoice = typeof InvoiceSchema.$inferSelect & {
  order?: Order;
  customer?: Customer;
  company_id?: string;
  delivery_address?: string;
  order_taken_by?: string;
  paynow_qr?: string;
}
export type Customer = typeof CustomerSchema.$inferSelect & { customer_contacts?: CustomerContact[]; customer_tags?: CustomerTag[] }
export type CustomerLocation = typeof CustomerLocationSchema.$inferSelect & { customer_contacts?: CustomerContact[] }
export type CustomerEmail = typeof CustomerEmailSchema.$inferSelect & { locations?: CustomerEmailLocation[] }
export type CustomerEmailLocation = typeof CustomerEmailLocationSchema.$inferSelect & {
  cl?: CustomerLocation;
  email?: CustomerEmail;
};
export type UserProfile = typeof UserProfileSchema.$inferSelect & { is_inactive?: boolean, user_managed_emails?: UserManagedEmail[] };
export type UserPermission = typeof UserPermissionSchema.$inferSelect
export type Message = typeof MessageSchema.$inferSelect
export type MessageV2 = typeof MessageV2Schema.$inferSelect
export type ProcessingMessage = typeof ProcessingMessageSchema.$inferSelect
export type AiModel = typeof AiModelSchema.$inferSelect
export type NotificationTemplate = typeof NotificationTemplateSchema.$inferSelect
export type OrderMessageClassification = typeof OrderMessageClassificationSchema.$inferSelect
export type TextToOrderSample = typeof TextToOrderSampleSchema.$inferSelect
export type WhatsappAccount = typeof WhatsappAccountSchema.$inferSelect & { user?: UserProfile }
export type ChatLocation = typeof ChatLocationSchema.$inferSelect & { customer_location: CustomerLocation, chat: Chat }
export type Chat = typeof ChatSchema.$inferSelect & { locations?: ChatLocation[] }
export type Unit = typeof UnitSchema.$inferSelect & { expansions?: UnitExpansion[] }
export type OrderGuide = typeof OrderGuideSchema.$inferSelect & { unit: Unit, product?: Product, customer?: Customer }
export type InviteMember = typeof InviteMemberSchema.$inferSelect
export type Category = typeof CategorySchema.$inferSelect
export type UnitCustomLabel = typeof UnitCustomLabelSchema.$inferSelect & { expansions?: UnitExpansion[] | null }
export type OauthCredentials = {
  erp_client_access_token?: string
  erp_user_access_token?: string
  erp_client_refresh_token?: string
  erp_user_refresh_token?: string
}
export type ErpIntegrationLog = typeof ErpIntegrationLogSchema.$inferSelect
export type BaseUnitEnum = 'ORDER_GUIDE_UNIT' | 'CATALOG_BASE_UNIT'
export type StandingOrderProduct = typeof StandingOrderProductSchema.$inferSelect
export type StandingOrder = typeof StandingOrderSchema.$inferSelect & { standing_order_products: StandingOrderProduct[] }
export type OrderCorrectionEvent = typeof OrderCorrectionEventSchema.$inferSelect & { user?: UserProfile | null }
export type UnitExpansion = typeof UnitExpansionSchema.$inferSelect

export enum OrderTypeEnum {
  ONE_TIME = 'ONE_TIME',
  RECURRING = 'RECURRING',
}

export enum OrderSourceEnum {
  MANUAL = 'MANUAL',
  BOT = 'BOT',
  FILE = 'FILE',
  ORDER_LINK = 'ORDER_LINK',
  TEXT = 'TEXT',
  MAIL_ATTACHMENT = 'MAIL_ATTACHMENT',
  STANDING_ORDER = 'STANDING_ORDER',
}

export enum FileTypeEnum {
  IMAGE = 'IMAGE',
  DOCUMENT = 'DOCUMENT',
}

type NotificationTemplateMapping = {
  source: string;
  example: string; // Show the example of the mapping, so we can display the sample text to user
};

export type NotificationTemplateConfiguration = Omit<
  NotificationTemplate,
  'id' | 'owned_by' | 'created_at' | 'updated_at'
> & {
  footer?: string;
  configurations: NotificationTemplateMapping[];
};

export enum NotificationTemplateType {
  ORDER_UPDATED = 'ORDER_UPDATED',
}
